
import { ReactElement, useEffect, useMemo, useState } from "react";

import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

import SoftBox from "./components/common/softBox";

import Sidenav from "./components/sidenav";
import Configurator from "./components/configurator";

import theme from "./assets/theme";
import themeRTL from "./assets/theme/theme-rtl";

import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache, { EmotionCache } from "@emotion/cache";

import routes, { IRoute } from "./routes";

import { useSoftUIController, setMiniSidenav, setOpenConfigurator } from "./context";

// import brand from "./assets/images/logo-ct.png";
import brand from "./assets/images/logo-sort.png";


import SignIn from "./pages/authentication/sign-in";


//Custom Css Starts
import { ToastContainer } from "react-toastify";
import "./App.css";
import "react-image-gallery/styles/css/image-gallery.css";
import 'react-toastify/dist/ReactToastify.css';
import { FaCog } from "react-icons/fa";
import { useAuth } from "./context/AuthProvider";
import ViewUser from "./pages/user/ViewUser";
import ViewOrderRequest from "./pages/order-requests/viewOrder";
import ViewCreditLimitRequest from "./pages/credit-limit-requests/viewCreditLimit";
import EditPromotion from "./pages/promotion/EditPomotions";
import AddPromotion from "./pages/promotion/AddPromotions";
import EditPublication from "./pages/publication/EditPublications";
import AddPublication from "./pages/publication/AddPublications";
import EditOtherService from "./pages/otherServices/EditOtherServices";
import AddOtherService from "./pages/otherServices/AddOtherServices";
import AddCreditSegmentCode from "./pages/creditSegmentCode/AddCreditSegmentCode";
import EditCreditSegmentCode from "./pages/creditSegmentCode/EditCreditSegmentCode";
import { registerServiceWorker } from "./config/firebase";
import ViewReturnSalesOrder from "./pages/return-sales-order/viewOrder";
import AddRole from "./pages/roles/AddRole";
import EditRole from "./pages/roles/EditRole";
import AddLocalization from "./pages/localization/AddLocalization";
import EditLocalization from "./pages/localization/EditLocalization";
import AddStaff from "./pages/staffs/AddStaff";
import EditStaff from "./pages/staffs/EditStaff";
import AddSalesOrderRequisitionConfig from "./pages/salesOrderRequisitionConfig/AddSalesOrderRequisitionConfig";
import EditSalesOrderRequisitionConfig from "./pages/salesOrderRequisitionConfig/EditSalesOrderRequisitionConfig";

function App() {
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, direction, layout, openConfigurator, sidenavColor } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState<EmotionCache | null>(null);
  const { pathname } = useLocation();
  const { user } = useAuth();
  // Cache for the rtl
  useMemo(() => {
    const cacheRtl: EmotionCache = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    if (document && document?.documentElement && document?.documentElement?.scrollTop) {
      document.documentElement.scrollTop = 0;
    }
    if (document && document?.scrollingElement && document?.scrollingElement?.scrollTop) {
      document.scrollingElement.scrollTop = 0;
    }
  }, [pathname]);

  const getRoutes: ReactElement<any, string | React.JSXElementConstructor<any>> | null | any = (allRoutes: [IRoute]) =>
    allRoutes.map((route: IRoute) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  const getOtherRoutes: ReactElement<any, string | React.JSXElementConstructor<any>> | null | any = () => (
    <>
      <Route path="/sign-in" element={<SignIn />} />
      <Route path="/users/view/:id" element={<ViewUser />} />
      <Route path="/publication/add" element={<AddPublication />} />
      <Route path="/publication/edit/:id" element={<EditPublication />} />
      <Route path="/promotion/add" element={<AddPromotion />} />
      <Route path="/promotion/edit/:id" element={<EditPromotion />} />
      <Route path="/role/add" element={<AddRole />} />
      <Route path="/role/edit/:id" element={<EditRole />} />
      <Route path="/other-services/add" element={<AddOtherService />} />
      <Route path="/other-services/edit/:id" element={<EditOtherService />} />
      <Route path="/order/view/:id" element={<ViewOrderRequest />} />
      <Route path="/return-sales-order/view/:id" element={<ViewReturnSalesOrder />} />
      <Route path="/credit-limit/view/:id" element={<ViewCreditLimitRequest />} />
      <Route path="/credit-segment/add" element={<AddCreditSegmentCode />} />
      <Route path="/credit-segment/edit/:id" element={<EditCreditSegmentCode />} />
      <Route path="/localization/add" element={<AddLocalization />} />
        <Route path="/localization/edit/:id" element={<EditLocalization />} />
      <Route path="/staffs/add" element={<AddStaff />} />
      <Route path="/staffs/edit" element={<EditStaff />} />
      <Route
        path="/sale-order-requisition-configuration/add"
        element={<AddSalesOrderRequisitionConfig />}
      />
      <Route
        path="/sale-order-requisition-configuration/edit/:id"
        element={<EditSalesOrderRequisitionConfig />}
      />
      <Route path="*" element={<Navigate to={user ? "/dashboard" : "/sign-in"} replace={true} />} />
    </>
  )

  const configsButton = (
    <SoftBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.5rem"
      height="3.5rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <FaCog size={20} />
    </SoftBox>
  );


  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <>
      {direction === "rtl" ? (
        <CacheProvider value={rtlCache}>
          <ThemeProvider theme={themeRTL}>
            <CssBaseline />
            {layout === "dashboard" && (
              <>
                <Sidenav
                  color={sidenavColor}
                  brand={brand}
                  brandName="Uni-Pharma"
                  routes={routes}
                  onMouseEnter={handleOnMouseEnter}
                  onMouseLeave={handleOnMouseLeave}
                />
                <Configurator />
                {configsButton}
              </>
            )}
            {layout === "vr" && <Configurator />}
            <Routes>
              {getRoutes(routes)}
              {getOtherRoutes()}
            </Routes>
          </ThemeProvider>
        </CacheProvider>
      ) : (
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {layout === "dashboard" && (
            <>
              <Sidenav
                color={sidenavColor}
                brand={brand}
                brandName="Uni-Pharma"
                routes={routes}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
              />
              <Configurator />
              {configsButton}
            </>
          )}
          {/* {layout === "vr" && <Configurator />} */}
          <Routes>
            {getRoutes(routes)}
            {getOtherRoutes()}
          </Routes>
        </ThemeProvider>
      )}
      <ToastContainer />
    </>
  );


}

export default App;
